import { all, call, put, select } from 'redux-saga/effects';
import { requestFailed } from '../../app/actions/RequestFailedAction';
import { fetchContentForInitialSlice, getFavoritesResourceQueryForContentTypes, getRecentsResourceQueryForContentTypes, getRecommendationsResourceQuery } from '../../app/sagas/spaces/content/MixedContentSagas';
import { selectFavoriteContentTypes } from '../../app/selectors/ContentFilterSelectors';
import { SessionSiteSelectors } from '../../app/selectors/SessionInfoSelectors';
import { Messages } from '../../codegen/Localize';
import { ListResult } from '../../ts/fetchers/ListResult';
import { Order } from '../../ts/helpers/Order';
import { ExplorableItemDefinitions } from '../../ts/models/ContentDefinitions';
import { IContentInfo } from '../../ts/models/IContentInfo';
import { OrderKeys } from '../../ts/models/OrderKeys';
import { favoritesSpaceDefaultSortOrder } from '../../ts/models/sort/FavoritesSortOptions';
import { recentsSpaceDefaultSortOrder } from '../../ts/models/sort/RecentsSortOptions';
import { ExploreContentApi_ContentType } from '../../ts/ServerApi/types/IContent';
import { getHomeFetchFavoritesSucceededAction, getHomeFetchPopularSucceededAction, getHomeFetchRecentsSucceededAction, getHomeFetchRecommendationsSucceededAction } from '../actions/HomeActions';
import { ChannelTypes } from '../types/ChannelTypes';
import { apiPerformanceTimer, HomeApiResult } from '../utils/ApiPerformanceTimer';
import { generateUuid } from '../utils/uuidGenerator';
import { fetchServerWelcomeChannelInfo } from './HomeSaga';

/**
 * The main function to get all the content for channels on Home, used in MixedContentSagas.ts
 */
export function* fetchHomeContent() {
  try {
    const isVizRecommendationsEnabled = yield select(SessionSiteSelectors.isVizRecommendationsEnabled);
    const pageViewId = generateUuid();

    yield all([
      call(apiPerformanceTimer, fetchHomeFavorites, ChannelTypes.favorites, pageViewId),
      call(apiPerformanceTimer, fetchHomeRecents, ChannelTypes.recents, pageViewId),
      call(fetchServerWelcomeChannelInfo),
      isVizRecommendationsEnabled ?
        call(apiPerformanceTimer, fetchHomeRecommendations, ChannelTypes.recommendations, pageViewId) :
        call(apiPerformanceTimer, fetchHomePopular, ChannelTypes.popular, pageViewId),
    ]);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* fetchHomeFavorites(result: HomeApiResult) {
  const favoriteContentTypes: ReadonlyArray<ExploreContentApi_ContentType> = yield select(selectFavoriteContentTypes);
  const favoritesResource = yield call(getFavoritesResourceQueryForContentTypes, favoriteContentTypes, favoritesSpaceDefaultSortOrder);
  const favoritesContent: ListResult<IContentInfo> = yield call(fetchContentForInitialSlice, favoritesResource);
  yield put(getHomeFetchFavoritesSucceededAction(favoritesContent));

  result.contentLength = getContentItemLength(favoritesContent);
}

export function* fetchHomeRecents(result: HomeApiResult) {
  const recentsResource = yield call(getRecentsResourceQueryForContentTypes, ['view'], recentsSpaceDefaultSortOrder);
  const recentsContent: ListResult<IContentInfo> = yield call(fetchContentForInitialSlice, recentsResource);
  yield put(getHomeFetchRecentsSucceededAction(recentsContent));

  result.contentLength = getContentItemLength(recentsContent);
}

export function* fetchHomePopular(result: HomeApiResult) {
  const popularResource = yield call(getPopularViewsResourceQuery);
  const popularContent: ListResult<IContentInfo> = yield call(fetchContentForInitialSlice, popularResource);
  yield put(getHomeFetchPopularSucceededAction(popularContent));

  result.contentLength = getContentItemLength(popularContent);
}

export function* fetchHomeRecommendations(result: HomeApiResult) {
  const recommendationsResource = yield call(getRecommendationsResourceQuery);
  const recommendationsContent: ListResult<IContentInfo> = yield call(fetchContentForInitialSlice, recommendationsResource);
  yield put(getHomeFetchRecommendationsSucceededAction(recommendationsContent));
  result.contentLength = getContentItemLength(recommendationsContent);
}

function* getPopularViewsResourceQuery() {
  try {
    const resource = ExplorableItemDefinitions['view'].getBasicQuery()
      .withOrder([Order.desc(OrderKeys.hitsLastOneMonthTotal)]);
    return resource;
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

function getContentItemLength(content: ListResult<IContentInfo>) {
  return (content && content.items && content.items.length) || 0;
}
